import { useEffect, useState } from 'react';
import type { ThemeName } from '../store/docStore';

/**
 * Read the app's *resolved* light/dark mode straight from the DOM
 * (`<html data-theme="dark|light">`, set by the store via `useTheme`) and keep
 * it live with a MutationObserver. This avoids threading a `dark` prop through
 * `App` → `RenderedPreview` (kept out of scope here): the preview's Shiki theme,
 * Mermaid theme, and chart theme all follow this hook on toggle.
 *
 * Returns `true` when the resolved theme is dark.
 */
export function usePreviewTheme(): boolean {
  const [dark, setDark] = useState<boolean>(() => readDark());

  useEffect(() => {
    const root = document.documentElement;
    const update = (): void => setDark(root.getAttribute('data-theme') === 'dark');
    update();
    const observer = new MutationObserver(update);
    observer.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  return dark;
}

function readDark(): boolean {
  if (typeof document === 'undefined') return false;
  return document.documentElement.getAttribute('data-theme') === 'dark';
}

const DEFAULT_THEME_NAME: ThemeName = 'sans-contrast';

function readThemeName(): ThemeName {
  if (typeof document === 'undefined') return DEFAULT_THEME_NAME;
  const v = document.documentElement.getAttribute('data-theme-name');
  if (
    v === 'sans-contrast' ||
    v === 'sans-reader' ||
    v === 'serif-contrast' ||
    v === 'serif-reader'
  ) {
    return v;
  }
  return DEFAULT_THEME_NAME;
}

/**
 * Read the app's *resolved* typography theme straight from the DOM
 * (`<html data-theme-name>`, set by the store via `useTheme`) and keep it live
 * with a MutationObserver. Same DOM-reading pattern as {@link usePreviewTheme}
 * so diagram renderers (Mermaid / Vega) can pick up the active PROSE font and
 * reader-vs-contrast palette without threading props through `App`.
 *
 * Returns `{ themeName, serif, reader }`:
 *  - `themeName` — the full `data-theme-name` value.
 *  - `serif` — prose typeface axis (true = Source Serif 4, false = Inter).
 *  - `reader` — palette axis (true = soft/low-contrast reader, false = crisp).
 */
export function useThemeName(): { themeName: ThemeName; serif: boolean; reader: boolean } {
  const [themeName, setThemeName] = useState<ThemeName>(() => readThemeName());

  useEffect(() => {
    const root = document.documentElement;
    const update = (): void => setThemeName(readThemeName());
    update();
    const observer = new MutationObserver(update);
    observer.observe(root, { attributes: true, attributeFilter: ['data-theme-name'] });
    return () => observer.disconnect();
  }, []);

  return {
    themeName,
    serif: themeName.startsWith('serif'),
    reader: themeName.endsWith('reader'),
  };
}
